import {createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode} from 'react'
import {announce} from '@github-ui/aria-live'
import {useUnifiedBatch} from '../../../stores/layout-selectors'
import type {UnifiedBatchItem} from '../../../stores/slices/unified-batch'

/**
 * Stable row identity for the override map and DOM ids. The store treats `(id, undefined)`
 * and `(id, 0)` as distinct, so a whole-comment's `undefined` index must not collapse onto `0`.
 */
export function batchRowKey(item: UnifiedBatchItem): string {
  return `${item.commentDatabaseId}:${item.suggestionIndex === undefined ? 'comment' : String(item.suggestionIndex)}`
}

/** Stable action callbacks — split from the dynamic state so memoized rows keep their identity. */
type BatchCommentCollapseActions = {
  /**
   * Toggle one row. When `expandAll` (alt-click) the row's new state is applied to every row.
   * `element` (the triggering control) scopes the toggle-all announcement so it isn't dropped
   * while the compact-mode dialog is open.
   */
  toggleRow: (item: UnifiedBatchItem, expandAll: boolean, element?: HTMLElement) => void
  /** Expand every row when any is collapsed, otherwise collapse every row. `element` as above. */
  toggleAll: (element?: HTMLElement) => void
}

type BatchCommentCollapseState = {
  isOpen: (item: UnifiedBatchItem) => boolean
  allExpanded: boolean
  hasRows: boolean
}

const BatchCommentCollapseActionsContext = createContext<BatchCommentCollapseActions | null>(null)
const BatchCommentCollapseStateContext = createContext<BatchCommentCollapseState | null>(null)

const rowDefaultsOpen = (item: UnifiedBatchItem) => !item.isOutdated
const isRowOpen = (item: UnifiedBatchItem, overrides: Record<string, boolean>) =>
  overrides[batchRowKey(item)] ?? rowDefaultsOpen(item)

/**
 * Owns the per-row open/closed override map so the list rows and the heading's `Toggle all`
 * drive the same state. Rows default open, outdated rows closed. Actions read state through
 * refs to stay referentially stable and preserve row memoization.
 */
export function BatchCommentCollapseProvider({children}: {children: ReactNode}) {
  const {items} = useUnifiedBatch()
  const [overrides, setOverrides] = useState<Record<string, boolean>>({})

  // Mirror state into refs (in an effect, never during render) so the stable action callbacks
  // can read it. User events read these refs only after commit, so they're current.
  const itemsRef = useRef(items)
  const overridesRef = useRef(overrides)
  useEffect(() => {
    itemsRef.current = items
    overridesRef.current = overrides
  }, [items, overrides])

  const setAllOpen = useCallback((open: boolean, element?: HTMLElement) => {
    setOverrides(Object.fromEntries(itemsRef.current.map(item => [batchRowKey(item), open])))
    // `announce` re-fires repeated identical messages (e.g. two alt-collapses in a row) that
    // a hand-rolled region would drop. Scope it to the triggering `element` so the compact-mode
    // Primer dialog doesn't swallow a document-level announcement.
    announce(open ? 'Expanded all batched comments' : 'Collapsed all batched comments', {element})
  }, [])

  const toggleRow = useCallback(
    (item: UnifiedBatchItem, expandAll: boolean, element?: HTMLElement) => {
      if (expandAll) {
        setAllOpen(!isRowOpen(item, overridesRef.current), element)
        return
      }
      const key = batchRowKey(item)
      // Derive from the latest override so two activations batched before a re-render
      // net back to the original state; the outdated default is the seed otherwise.
      setOverrides(prev => ({...prev, [key]: !(key in prev ? prev[key] : rowDefaultsOpen(item))}))
    },
    [setAllOpen],
  )

  const toggleAll = useCallback(
    (element?: HTMLElement) => {
      const allOpen =
        itemsRef.current.length > 0 && itemsRef.current.every(item => isRowOpen(item, overridesRef.current))
      setAllOpen(!allOpen, element)
    },
    [setAllOpen],
  )

  const actions = useMemo(() => ({toggleRow, toggleAll}), [toggleRow, toggleAll])

  const state = useMemo<BatchCommentCollapseState>(
    () => ({
      isOpen: item => isRowOpen(item, overrides),
      allExpanded: items.length > 0 && items.every(item => isRowOpen(item, overrides)),
      hasRows: items.length > 0,
    }),
    [overrides, items],
  )

  return (
    <BatchCommentCollapseActionsContext value={actions}>
      <BatchCommentCollapseStateContext value={state}>{children}</BatchCommentCollapseStateContext>
    </BatchCommentCollapseActionsContext>
  )
}

export function useBatchCommentCollapseActions(): BatchCommentCollapseActions {
  const actions = use(BatchCommentCollapseActionsContext)
  if (!actions) throw new Error('useBatchCommentCollapseActions must be used within BatchCommentCollapseProvider')
  return actions
}

export function useBatchCommentCollapseState(): BatchCommentCollapseState {
  const state = use(BatchCommentCollapseStateContext)
  if (!state) throw new Error('useBatchCommentCollapseState must be used within BatchCommentCollapseProvider')
  return state
}
